/**
 * Analyzer: ATM Bar Context
 *
 * Bar-grounded observations scoped to a SINGLE ATM strategy. Different from
 * `setupBarContext` (one setup, all of its history) and `dayBarContext`
 * (one day, all setups) — this one zooms into a specific ATM's tracked
 * history and asks: is this management style actually doing its job?
 *
 * Where setupBarContext asks "is the ENTRY edge real," this asks "is the
 * EXIT scaffolding (stop placement, target placement, trail timing) real."
 *
 * Three patterns this round:
 *
 *   1. Stop-too-tight. Across this ATM's losers, if the average MAE/R is
 *      pushing the full 1.0R risk while typical winners showed early
 *      adverse excursion before working, the stop is at a level the market
 *      routinely reaches before reversing. Surface so the trader knows
 *      this ATM's stop placement is the wrong tail of the distribution.
 *
 *   2. Target leakage. Mirror of setup-scope MFE leakage but framed as a
 *      placement problem: when this ATM's targets cap winners well below
 *      the typical favorable move, the targets are placed inside the
 *      profit-taking zone, not at it.
 *
 *   3. ATM decay. Recent 14 days vs prior 14 days WR comparison — flags
 *      ATMs whose effectiveness has dropped meaningfully. Could be regime
 *      shift (volatility expanded, the ATM's stop is now too tight for
 *      it), or could be a setup-mix change. Either way, surface for review.
 *
 * Reads:
 *   trade.atmStrategy, trade.netPnl, trade.date
 *   trade.barAnalysis.{maeR, mfeR, mfeDollars}
 *
 * Trades without barAnalysis are silently skipped — backfill (Settings →
 * Charts → Run backfill) is the prerequisite for full signal here.
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';

// Thresholds — same conservative shape as setupBarContext
const STOP_TIGHT_MIN_LOSERS    = 4;
const STOP_TIGHT_MIN_AVG_MAER  = 0.85;  // losers averaged ≥85% of 1R adverse before stop
const STOP_TIGHT_MIN_MFER      = 0.30;  // AND showed at least 30% favorable first
const LEAKAGE_MIN_WINNERS      = 4;
const LEAKAGE_MAX_AVG_CAPTURE  = 0.40;
const DECAY_MIN_TRADES_PER_HALF = 3;
const DECAY_MIN_WR_DROP        = 0.20;
const DECAY_RECENT_DAYS        = 14;
// HTF stack-edge thresholds — same as setup-scope so users see consistent
// behavior across the two cards.
const STACK_GAP_MIN_TRADES     = 3;
const STACK_GAP_MIN_WR_GAP     = 0.20;

const fmtPct = (n) => `${Math.round(n * 100)}%`;

const ba = (t) => {
  const a = t?.barAnalysis;
  if (!a || typeof a !== 'object') return null;
  if (!Number.isFinite(a.maeR) || !Number.isFinite(a.mfeR)) return null;
  return a;
};

// Period-list-aware EMA stack classifier (mirrors dayBarContext / setupBarContext).
// Returns 'with' | 'against' | 'neutral' relative to the trade's direction.
const classifyStack = (emas, direction) => {
  if (!emas || typeof emas !== 'object') return 'neutral';
  const periods = Object.keys(emas)
    .map(k => parseInt(k, 10))
    .filter(n => Number.isFinite(n) && n >= 1)
    .sort((a, b) => a - b);
  if (periods.length < 2) return 'neutral';
  const values = periods.map(p => emas[p] ?? emas[String(p)]);
  if (!values.every(v => Number.isFinite(v))) return 'neutral';
  let bullish = true, bearish = true;
  for (let i = 0; i < values.length - 1; i++) {
    if (values[i] <= values[i + 1]) bullish = false;
    if (values[i] >= values[i + 1]) bearish = false;
  }
  if (!bullish && !bearish) return 'neutral';
  const isLong = direction !== 'short';
  if (bullish) return isLong ? 'with' : 'against';
  return isLong ? 'against' : 'with';
};

export const key = 'atmBarContext';
export const tier = 'free';

// ── Pattern 0: Per-HTF stack-alignment WR gap ─────────────────────────────
//
// Same shape as setupBarContext.detectHtfStackEdge but framed for ATM:
// "does THIS exit scaffolding hold up when the bigger trend agrees?"
//
// Why an ATM has a per-HTF edge:
//   - BE-then-trail style ATMs depend on continuation. They shine when the
//     4H/1H trend extends — and underperform when price chops counter to
//     bigger structure (every reclaim trips BE, every pullback hits the
//     trailing stop).
//   - Wider-stop / fixed-target ATMs need MFE depth, which is more frequent
//     when HTF agrees. Same pattern, different mechanism.
//   - A counter-{tf} ATM is a real thing — wide stops, no trailing, fixed
//     R:R — and it's expected to underperform when the HTF DOES agree
//     because the ATM is trying to fade what's working.
//
// Each HTF that meets the sample-size + gap threshold produces its own
// observation. Higher-TF observations carry higher priority because
// fighting 4H is the most expensive ATM mismatch.

const detectHtfStackEdgeForTf = (atmName, trades, tf) => {
  const classified = trades
    .map(t => ({ trade: t, stance: classifyStack(ba(t)?.htfEmas?.[tf], t.direction) }))
    .filter(c => c.stance !== 'neutral');

  const withStack    = classified.filter(c => c.stance === 'with');
  const againstStack = classified.filter(c => c.stance === 'against');
  if (withStack.length < STACK_GAP_MIN_TRADES || againstStack.length < STACK_GAP_MIN_TRADES) return null;

  const wr = (group) => group.length === 0 ? 0
    : group.filter(c => c.trade.netPnl > 0).length / group.length;
  const withWR    = wr(withStack);
  const againstWR = wr(againstStack);
  const gap = Math.abs(withWR - againstWR);
  if (gap < STACK_GAP_MIN_WR_GAP) return null;

  const withBetter = withWR > againstWR;
  const tfLabel = tf.toUpperCase();

  return makeObservation({
    id: `${key}:${atmName}:htf-stack-edge:${tf}`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: withBetter ? SEVERITY.SUCCESS : SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    title: withBetter
      ? `"${atmName}" works when the ${tfLabel} stack agrees`
      : `"${atmName}" is misfiring with the ${tfLabel} — counter-${tfLabel} ATM`,
    detail: `${withStack.length} ${tfLabel}-aligned trades: ${fmtPct(withWR)} WR. ${againstStack.length} against-${tfLabel}: ${fmtPct(againstWR)} WR. Gap of ${fmtPct(gap)}.`,
    action: withBetter
      ? `Pin this ATM to ${tfLabel}-aligned setups only — the exit scaffolding (BE/trail/targets) needs continuation to pay. Counter-${tfLabel} setups need a different ATM.`
      : `This ATM is built for fading bigger structure — wider stops or fixed targets that don't need trend extension. Reserve it for counter-${tfLabel} plays and use a different ATM when ${tfLabel} agrees.`,
    evidence: {
      tf,
      withCount: withStack.length,
      againstCount: againstStack.length,
      withWR,
      againstWR,
      gap,
    },
    // Same priority ordering as setup-scope: 4h > 1h > 15m. ATMs are even
    // more sensitive to the bigger trend so we set the floor a notch above
    // entry-TF analysis (which the ATM scope doesn't have anyway).
    priority: tf === '4h' ? 86 : tf === '1h' ? 84 : tf === '15m' ? 82 : 80,
    confidence: 0.82,
  });
};

const detectHtfStackEdge = (atmName, trades) => {
  const htfsPresent = new Set();
  for (const t of trades) {
    const map = ba(t)?.htfEmas;
    if (map && typeof map === 'object') {
      for (const tf of Object.keys(map)) htfsPresent.add(tf);
    }
  }
  if (htfsPresent.size === 0) return [];

  // Stable order so panel + tests are deterministic.
  const tfOrder = ['15m', '30m', '1h', '2h', '4h'];
  const ordered = [...htfsPresent].sort((a, b) => {
    const ai = tfOrder.indexOf(a);
    const bi = tfOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const out = [];
  for (const tf of ordered) {
    const obs = detectHtfStackEdgeForTf(atmName, trades, tf);
    if (obs) out.push(obs);
  }
  return out;
};

// ── Pattern 1: Stop-too-tight (ATM-unique) ────────────────────────────────

const detectStopTooTight = (atmName, trades) => {
  const losers = trades.filter(t => {
    const a = ba(t);
    if (!a) return false;
    return Number.isFinite(t.netPnl) && t.netPnl < 0;
  });
  if (losers.length < STOP_TIGHT_MIN_LOSERS) return null;

  let sumMaeR = 0;
  let sumMfeR = 0;
  let touchedFullR = 0;
  for (const t of losers) {
    const a = ba(t);
    sumMaeR += a.maeR;
    sumMfeR += a.mfeR;
    if (a.maeR >= 0.95) touchedFullR++;
  }
  const avgMaeR = sumMaeR / losers.length;
  const avgMfeR = sumMfeR / losers.length;

  if (avgMaeR < STOP_TIGHT_MIN_AVG_MAER) return null;
  if (avgMfeR < STOP_TIGHT_MIN_MFER) return null;

  return makeObservation({
    id: `${key}:${atmName}:stop-too-tight`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `"${atmName}" stop sits where the market routinely reverses`,
    detail: `${losers.length} losing trades averaged ${avgMaeR.toFixed(2)}R against before the stop fired, but also averaged ${avgMfeR.toFixed(2)}R favorable first. ${touchedFullR}/${losers.length} touched the full 1R adverse mark.`,
    action: `Compare your stop placement against the wick distribution on ${atmName} entries. Loosening to 1.2-1.3R or moving the stop to a structural level often recovers these.`,
    evidence: {
      loserCount: losers.length,
      avgMaeR,
      avgMfeR,
      touchedFullR
    },
    confidence: 0.78,
    priority: 76
  });
};

// ── Pattern 2: Target leakage on the ATM's winners ────────────────────────

const detectAtmTargetLeakage = (atmName, trades) => {
  const winners = trades.filter(t => {
    const a = ba(t);
    if (!a) return false;
    if (!Number.isFinite(t.netPnl) || t.netPnl <= 0) return false;
    return Number.isFinite(a.mfeDollars) && a.mfeDollars > 0;
  });
  if (winners.length < LEAKAGE_MIN_WINNERS) return null;

  let sum = 0;
  let worst = null;
  let worstCapture = 1;
  for (const t of winners) {
    const cap = Math.max(0, Math.min(1, t.netPnl / ba(t).mfeDollars));
    sum += cap;
    if (cap < worstCapture) { worstCapture = cap; worst = t; }
  }
  const avgCapture = sum / winners.length;
  if (avgCapture >= LEAKAGE_MAX_AVG_CAPTURE) return null;

  const jumpActions = worst?.id
    ? [makeAction({
        type: ACTION_TYPE.JUMP_TO_TRADE,
        label: 'Show worst trade',
        payload: { tradeId: worst.id, date: worst.date || null },
        doneLabel: 'Jumping…',
      })]
    : null;

  return makeObservation({
    id: `${key}:${atmName}:target-leakage`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `"${atmName}" targets cap winners at ${fmtPct(avgCapture)} of MFE`,
    detail: `Across ${winners.length} winning trades on this ATM, average exit captured ${fmtPct(avgCapture)} of the available favorable move. Worst case: ${fmtPct(worstCapture)}.`,
    action: `Push T2/T3 further out, or convert one target into a runner with a structural trail. The market is delivering more than the ATM is asking for.`,
    actions: jumpActions,
    evidence: {
      avgCapture,
      winnerCount: winners.length,
      worstTradeId: worst?.id ?? null,
      worstCapture
    },
    confidence: 0.8,
    priority: 72
  });
};

// ── Pattern 3: ATM decay — recent half vs prior half ──────────────────────

const detectAtmDecay = (atmName, trades, anchorDate) => {
  if (!anchorDate || !Array.isArray(trades) || trades.length === 0) return null;

  const dateMinusDays = (dateStr, days) => {
    const [y, m, d] = dateStr.split('-').map(n => parseInt(n, 10));
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() - days);
    return dt.toISOString().slice(0, 10);
  };
  const recentFrom = dateMinusDays(anchorDate, DECAY_RECENT_DAYS);
  const priorFrom  = dateMinusDays(anchorDate, DECAY_RECENT_DAYS * 2);

  const recent = trades.filter(t => t.date && t.date >= recentFrom && t.date <= anchorDate);
  const prior  = trades.filter(t => t.date && t.date >= priorFrom && t.date < recentFrom);
  if (recent.length < DECAY_MIN_TRADES_PER_HALF) return null;
  if (prior.length  < DECAY_MIN_TRADES_PER_HALF) return null;

  const wr = (group) => group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length / group.length;
  const recentWR = wr(recent);
  const priorWR  = wr(prior);
  const drop = priorWR - recentWR;
  if (drop < DECAY_MIN_WR_DROP) return null;

  return makeObservation({
    id: `${key}:${atmName}:decay`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    title: `"${atmName}" decaying — WR dropped from ${fmtPct(priorWR)} to ${fmtPct(recentWR)}`,
    detail: `Last ${DECAY_RECENT_DAYS} days: ${recent.filter(t => t.netPnl > 0).length}/${recent.length} winners. Prior ${DECAY_RECENT_DAYS} days: ${prior.filter(t => t.netPnl > 0).length}/${prior.length}. Could be regime shift, setup-mix change, or stop tightness against expanded volatility.`,
    action: `Pull recent losers vs prior winners side by side. If MAE expanded with volatility, the ATM's stop scale needs to follow. If setups changed, the ATM may not fit the new mix.`,
    evidence: {
      recentWR,
      priorWR,
      drop,
      recentCount: recent.length,
      priorCount: prior.length,
    },
    confidence: 0.78,
    priority: 75
  });
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { atmName, atmTrades, anchorDate } = ctx || {};
  if (!atmName || !Array.isArray(atmTrades) || atmTrades.length === 0) return null;

  const out = [];

  // HTF stack edge first — answers "is this ATM matched to the right
  // structural regime?" before we get into the per-trade execution patterns.
  const htfEdges = detectHtfStackEdge(atmName, atmTrades);
  for (const o of htfEdges) out.push(o);

  const stopTight = detectStopTooTight(atmName, atmTrades);
  if (stopTight) out.push(stopTight);

  const leakage = detectAtmTargetLeakage(atmName, atmTrades);
  if (leakage) out.push(leakage);

  const decay = detectAtmDecay(atmName, atmTrades, anchorDate);
  if (decay) out.push(decay);

  return out.length === 0 ? null : out;
};

// Exports for tests
export {
  classifyStack,
  detectHtfStackEdge,
  detectHtfStackEdgeForTf,
  detectStopTooTight,
  detectAtmTargetLeakage,
  detectAtmDecay
};
